/*
 * Authors: mariushirjanu, Triadi, Koen
 * Created: 01/07/2019
 */
import { AfterViewChecked, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { IObject } from '@type_definitions/datatypes';
import { Chart } from '../chart/base-chart.component';
import {
  DashboardCode,
  DashboardMetadataService,
  IDashboardComponent,
  IDashboardExtraSectionStatus,
  IDashboardExtraSectionStatusValue,
  IDashboardExtraSectionStatusValueCheckType, IDashboardGroupMetadata,
  IDashboardMetadata,
  IDashboardSectionColumn,
  IDashboardSectionMetadata,
  IDashboardSectionValidation,
  IDashboardSort,
  IRowCountCtx
} from './dashboard-metadata.service';
import { DashboardService } from './dashboard.service';
import {
  IReportLayoutSetting,
  IReportAggregates,
  IReportData,
  IReportMetadata,
  IReportResponse,
  IReportValue,
  ISubColumns,
  ReportDataService
} from '@app/shared/report/report-data.service';
import _ from 'underscore';
import { util } from '@app/shared/util';
import { ReportFiltersUtil } from '@app/shared/report/report-filters-util.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { BrowseListConfigService, IBrowseListConfig } from '@app/shared/browselists/browselists-config.service';
import { STATE_CHART_BP, STATE_CHART_CUSTOMER } from '@app/shared/app-states';
import { StateService, UiOnParamsChanged, RawParams } from '@uirouter/angular';
import { GaMessageService } from '@app/shared/message/ga-message.service';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { TranslationCategory } from '@app/shared/services/ga-state.service';
import { TitleService } from '@app/shared/services/title.service';
import { BodyClassService } from '@app/shared/services/body-class.service';
import { IDashboardValidatorFn } from './filters/base-dashboard-filters.component';
import { ICONS, IDashboardExtraIcon } from './dashboard.model';
import { ACSL_COMMENT_KEY, CUSTOM_MENU_TEXT_ACSL_OTHERS, IEditableTextOption, OTHERS_COMMENT_KEY } from '@app/shared/editable-text/editable-text.service';
import { IDashboardMetadataReports, IDashboardStoredReports } from '@app/shared/report/dashboard-data.service';
import { GaEventService, IGAEvent } from '@app/shared/services/ga-event.service';
import { EVENT_DASHBOARD_REMOVE_REPORTS } from '@app/shared/app-events';
import { Subscription } from 'rxjs';

export interface IDashboardSectionStateRef {
  state:string;
  params:any;
}

interface IDashboardReportSectionBase {
  id?:number;
  browseListCode?:string;
  comments?:string;
  ref?:IDashboardSectionStateRef;
}

interface IDashboardReportSectionExtraBase extends IDashboardReportSectionBase {
  name:string;
}

interface IDashboardReportSectionExtraTextValue {
  label?:string;
  report?:IDashboardReportSectionExtraBase;
}

interface IDashboardReportSection extends IDashboardReportSectionBase {
  init?:Promise<void | IDashboardSection>;
  metadata?:IReportMetadata;
  gridMetadata?:IReportMetadata;
  loading?:boolean;
  initializing?:boolean;
  data?:IReportData;
  subColumns?:ISubColumns;
  rowCount?:number; // used to save the total number of rows for group count calculation
  indicator?:number|string; // indicator contains the row counter or custom string/number value by applying indicatorFn()
  // 'appliedFilters' is coming from dashboard filters selection
  appliedFilters?:IObject;
  // 'queryFilters' is combined of dashboard metadata default filters and stored report filters (if provided in embedded report)
  queryFilters?:IObject;
  sort?:IDashboardSort[];
  aggregates?:IReportAggregates;
  extraStatus?:IDashboardExtraSectionStatus;
  infoComponent?:IDashboardComponent;
  indicatorFn?:(data:IReportData, ctx?:IRowCountCtx) => number | string;
  gridHeight?:string;
  storedReport?:string; // use stored report view
  browseListConfig?:IBrowseListConfig; // browselist config
  validations?:IDashboardSectionValidation[];
  layoutSetting?:IReportLayoutSetting;
  layoutGridCode?:string;
}

export interface IDashboardSection {
  id?:string;
  key:string;
  name:string;
  code:string;
  screen:string;
  collapsed:boolean;
  report:IDashboardReportSection;
  extraIcon?:IDashboardExtraIcon;
  extraTextValue?:IDashboardReportSectionExtraTextValue;
  partialRender?:boolean; // this is input binding to use ng-if or ng-show in collapsible pane component
}

export interface IDashboardGroup {
  key:string;
  collapsed:boolean;
  loading?:boolean;
  rowCount?:number;
  indicator?:string;
  sections:IDashboardSection[];
  name:string;
  info?:string;
}

const NUM_CONCURRENT_SEARCHES:number = 2;

@Component({
  selector: 'dashboard',
  template: `
    <h1 class="dashboard-title">{{ title }}</h1>
    
    <div *ngIf="authorizedReports?.length && !loadingReportPermissions">
      <div class="row">
        <div class="dashboard-subtitle col-sm-9">
          <text [customMenu]="subtitleCustomMenu" [key]="subtitleKey"></text>
          <ndc-dynamic [ndcDynamicComponent]="subtitleComponent?.name" [ndcDynamicInputs]="subtitleComponent?.inputs"></ndc-dynamic>
        </div>
      </div>
  
      <div class="row">
        <div class="collapsible-title-extra col-12">
          <div class="dashboard-filters-section">
            <!-- when there is more than one dashboard, we can replace this with a component that is dynamically loaded depending on the dashboard -->
            <ndc-dynamic [ndcDynamicComponent]="filtersComponent?.name" [ndcDynamicInputs]="filtersComponent?.inputs" [ndcDynamicOutputs]="filtersComponent?.outputs"></ndc-dynamic>
  
            <button class="btn btn-danger cancel-button" *ngIf="loading" (click)="cancelSearch()" translate>GA.BUTTONS.STOP_SEARCH</button>
            <span class="section-buttons">
              <button class="btn collapse-all-button" (click)="collapseAll()"><i class="fa fa-compress"></i> <span translate>GA.GENERAL.COLLAPSE_ALL</span></button>
              <a class="btn chart-button" [href]="getChartLink()" (click)="goToChart($event)" title="Chart KPI" *ngIf="chartLink" target="_blank"><i class="fa fa-bar-chart"></i> <span>Chart</span></a>
              <a class="btn training-button" href="/ga2/#/faqs?title=FAQ%20Search&query=training%20pack" target="_blank" title="Training Packs" *ngIf="dashboardCode === '${DashboardCode.OD_ORDER}'"><i class="fa fa-info-circle"></i> <span>Training Packs</span></a>
              <a class="btn reports-button" href="/ga2/#/reports/94?storedReport=CUSTOMER_KPI_MOST_USED_REPORT&showfilters=false&autoload=true&title=Most%20used%20reports" target="_blank" *ngIf="dashboardCode === '${DashboardCode.CUSTOMER}'">
                <i class="fa fa-table"></i> <span translate>DASHBOARD.CUSTOMER.REPORTS.LABEL</span>
              </a>
            </span>
          </div>
  
          <div class="dashboard-comments" *ngIf="comments" [innerHTML]="comments"></div>
  
          <ndc-dynamic [ndcDynamicComponent]="headerComponent?.name" [ndcDynamicInputs]="headerComponent?.inputs" [ndcDynamicOutputs]="headerComponent?.outputs"></ndc-dynamic>
  
          <dashboard-sections
            [dashboardCode]="dashboardCode"
            [sections]="sections"
            [groups]="groups"
            [getInfoComponentInputs]="getInfoComponentInputs"
            [dashboardMetadataReports]="dashboardMetadataReports"
            [dashboardStoredReports]="dashboardStoredReports"
            (onRefreshSections)="refreshSections($event.sections, $event.group)"
          ></dashboard-sections>
  
          <ndc-dynamic [ndcDynamicComponent]="footerComponent?.name" [ndcDynamicInputs]="footerComponent?.inputs" [ndcDynamicOutputs]="footerComponent?.outputs"></ndc-dynamic>
        </div>
      </div>
    </div>
    
    <div class="alert alert-warning" *ngIf="!authorizedReports?.length && !loadingReportPermissions">
      <i class="fa fa-warning"></i>&nbsp; <text class="d-inline-block" key="NO_ACCESS" screen="DASHBOARD"></text>
    </div>

    <activity-indicator *ngIf="loadingReportPermissions"></activity-indicator>
  `,
  styleUrls: [ './dashboard.scss' ],
  encapsulation: ViewEncapsulation.None
})
export class DashboardComponent implements OnInit, OnChanges, AfterViewChecked, OnDestroy, UiOnParamsChanged {
  @Input() public dashboardCode:DashboardCode;
  @Input() public companyCountryId:string;
  @Input() public countryId:string;
  @Input() public showFiltersParameter:boolean;
  @Input()
  public get filters():IObject {
    return this._filters;
  }

  public set filters(filters:IObject) {
    this._filters = filters;
    // update dynamic component input filter binding
    this.filtersComponent.inputs.filters = filters;
    this.headerComponent.inputs.filters = filters;
    this.footerComponent.inputs.filters = filters;

    _.each(this.sections, (section:IDashboardSection) => {
      if (section.report.infoComponent) {
        section.report.infoComponent.inputs.filters = filters;
      }
    });
  }

  public groups:IDashboardGroup[] = [];
  public screen:string;
  public category:TranslationCategory = TranslationCategory.DASHBOARD; // the category cannot be auto-assigned by using the current module because the dashboard can be loaded from other modules (e.g. bp profile)
  public title:string;
  public comments:string;
  public validator:Function;
  public chartLink:Chart;
  public dashboardMetadataReports:IDashboardMetadataReports;
  public dashboardStoredReports:IDashboardStoredReports;
  public filtersComponent:IDashboardComponent = {
    inputs: {},
    outputs: {}
  };
  public headerComponent:IDashboardComponent = {
    inputs: {},
    outputs: {}
  };
  public footerComponent:IDashboardComponent = {
    inputs: {},
    outputs: {}
  };
  public subtitleKey:string;
  public subtitleCustomMenu:IEditableTextOption[] = CUSTOM_MENU_TEXT_ACSL_OTHERS;
  public subtitleComponent:IDashboardComponent = {
    inputs: {}
  };
  public authorizedReports:number[];
  public loadingReportPermissions:boolean;

  private sectionIdx:number = 1;
  private isACSLUser:boolean;
  private sectionsToLoad:IDashboardSection[];   // stores the list of all sections presently loading data
  private eventHandler:Subscription;

  protected _filters:IObject;
  protected _loading:boolean = false;
  protected _sections:IDashboardSection[];

  private dashboardMetadata:IDashboardMetadata;

  constructor(
    private authService:AuthService,
    private dashboardService:DashboardService,
    private reportDataService:ReportDataService,
    private reportFiltersUtil:ReportFiltersUtil,
    private bodyClassService:BodyClassService,
    private formattingService:FormattingService,
    private stateService:StateService,
    private browseListConfigService:BrowseListConfigService,
    private dashboardMetadataService:DashboardMetadataService,
    private titleService:TitleService,
    private translationService:TranslationService,
    private messageService:GaMessageService,
    private changeDetectorRef:ChangeDetectorRef,
    private eventService:GaEventService
  ) {
    /***/
  }

  public ngOnInit():void {
    this.bodyClassService.add('dashboard');
    this.isACSLUser = this.authService.checkAcslUser();
    this.subtitleKey = this.isACSLUser ? ACSL_COMMENT_KEY : OTHERS_COMMENT_KEY;

    if (this.dashboardCode) {
      this.onDashboardCodeChange();
    }

    // register listener
    this.listen();
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.dashboardCode && changes.dashboardCode.currentValue !== this.dashboardCode) {
      this.dashboardCode = changes.dashboardCode.currentValue;
      this.onDashboardCodeChange();
    }
  }

  public ngAfterViewChecked():void {
    this.changeDetectorRef.detectChanges();
  }

  public uiOnParamsChanged = (newValues:IObject):void => {
    const filters:IObject = this.reportFiltersUtil.paramToFilters(newValues?.filters);
    this.filters = util.convertPropValueToNumber(filters);
  };

  private listen():void {
    this.eventHandler = this.eventService.events.subscribe((event:IGAEvent) => {
      if (event.name === EVENT_DASHBOARD_REMOVE_REPORTS) {
        this.removeSections(event.data);
      }
    });
  }

  public get sections():IDashboardSection[] {
    return this._sections;
  }

  public set sections(sections:IDashboardSection[]) {
    this._sections = sections;

    // update dynamic component input sections binding
    this.filtersComponent.inputs.sections = sections;
    this.headerComponent.inputs.sections = sections;
    this.footerComponent.inputs.sections = sections;
  }

  public get loading():boolean {
    return this._loading;
  }

  public set loading(loading:boolean) {
    this._loading = loading;
    // update dynamic component input loading binding
    this.filtersComponent.inputs.loading = loading;
    this.headerComponent.inputs.loading = loading;
    this.footerComponent.inputs.loading = loading;
  }

  private installComponents():void {
    // add getter / setter on every property added as default input bindings
    // to make sure dynamic component get the latest data
    const defaultInputBindings:IObject = {
      sections: this.sections,
      filters: this.filters,
      showFiltersParameter: this.showFiltersParameter,
      loading: this.loading,
      dashboardMetadata: this.dashboardMetadata
    };

    const defaultOutputBindings:IObject = {
      onSearch: this.startSearch,
      onFilterChange: this.updateFilters,
      onValidatorRegister: this.registerValidator
    };

    const extraInputBindings:IObject = {
      dashboardCode: this.dashboardCode,
      companyCountryId: this.companyCountryId,
      countryId: this.countryId
    };

    // This is where we would initialize the dynamic component from the dashboard metadata
    this.filtersComponent = {
      name: this.dashboardMetadata.filterComponent.name,
      inputs: _.extend(this.filtersComponent.inputs, defaultInputBindings, this.dashboardMetadata.filterComponent.inputs, extraInputBindings),
      outputs: defaultOutputBindings
    };

    if (this.dashboardMetadata.headerComponent) {
      this.headerComponent = {
        name: this.dashboardMetadata.headerComponent.name,
        inputs: _.extend(this.headerComponent.inputs, defaultInputBindings, this.dashboardMetadata.headerComponent.inputs),
        outputs: _.extend({}, defaultOutputBindings)
      };
    }

    if (this.dashboardMetadata.footerComponent) {
      this.footerComponent = {
        name: this.dashboardMetadata.footerComponent.name,
        inputs: _.extend(this.footerComponent.inputs, defaultInputBindings, this.dashboardMetadata.footerComponent.inputs),
        outputs: _.extend({}, defaultOutputBindings)
      };
    }

    if (this.dashboardMetadata.subtitleComponent) {
      this.subtitleComponent = {
        name: this.dashboardMetadata.subtitleComponent.name,
        inputs: _.extend(this.subtitleComponent.inputs, defaultInputBindings, this.dashboardMetadata.subtitleComponent.inputs),
        outputs: _.extend({}, defaultOutputBindings)
      };
    }
  }

  private installSections():void {
    this.getAuthorizedReports()
      .then((authorizedReports:number[]) => {
        this.authorizedReports = authorizedReports;
      })
      .then(() => {
        this.dashboardService.dashboardReports = _.chain(this.dashboardMetadata.reports)
          .filter((sectionMetadata:IDashboardSectionMetadata) => this.isReportAuthorized(sectionMetadata))
          .each((sectionMetadata:IDashboardSectionMetadata)=> {
            this.sections.push(this.createSection(sectionMetadata));
          })
          .value();

        this.dashboardService.createReportRefs(this.sections);
      });
  }

  private installGroups():void {
    if(this.dashboardMetadata?.groups?.length) {
      this.dashboardService.dashboardGroupReports = this.dashboardMetadata.groups;

      this.getAuthorizedReports()
        .then((authorizedReports:number[]) => {
          this.authorizedReports = authorizedReports;
        })
        .then(() => {
          let groupId:number = 1;
          _.each(this.dashboardService.dashboardGroupReports, (groupMetadata:IDashboardGroupMetadata) => {
            const sections:IDashboardSection[] = _.chain(groupMetadata.reports)
              .filter((sectionMetadata:IDashboardSectionMetadata) => this.isReportAuthorized(sectionMetadata))
              .map((sectionMetadata:IDashboardSectionMetadata)=> this.createSection(sectionMetadata))
              .value();

            this.groups.push({
              key: groupMetadata.key,
              collapsed: groupId !== 1,
              sections,
              name: `${this.screen}.${groupMetadata.key}.TITLE`,
              info: `${this.screen}.${groupMetadata.key}.INFO`
            });

            groupId++;
          });

        });
    }
  }

  private getAuthorizedReports():Promise<number[]> {
    let dashboardSections:IDashboardSectionMetadata[] = this.dashboardMetadata.reports || [];

    // when the dashboard has groups, loop on each group to fetch the sections
    if(this.hasDashboardGroups){
      _.each(this.dashboardService.dashboardGroupReports, (groupMetadata:IDashboardGroupMetadata) => {
        dashboardSections = dashboardSections.concat(groupMetadata.reports);
      });
    }

    // retrieve all report ids in a unique array
    const allReportIds:number[] = _.chain(dashboardSections)
      .filter(sectionMetadata => !sectionMetadata.restricted)
      .map((sectionMetadata:IDashboardSectionMetadata) => {
        if(sectionMetadata.id){
          return sectionMetadata.id;
        }
        const browselistConfig:IBrowseListConfig = this.browseListConfigService.getConfig(sectionMetadata.browselistCode);
        return browselistConfig.reportId;
      })
      .uniq()
      .value();

    if(this.isACSLUser){
      return Promise.resolve(allReportIds);
    }

    // filter report ids on the server by removing the ones which are not authorized and recreate
    this.loadingReportPermissions = true;
    return this.reportDataService.getAuthorizedReports(allReportIds)
      .finally(() => {
        this.loadingReportPermissions = false;
      });
  }

  private isReportAuthorized(sectionMetadata:IDashboardSectionMetadata):boolean {
    // for ACSL users do not check report permissions on the server as they are authorized to see all reports
    if(this.isACSLUser){
      return true;
    }

    const checkReportAuthorization:Function = (reportId:number):boolean => {
      return !sectionMetadata.restricted && _.contains(this.authorizedReports, reportId);
    };

    if(sectionMetadata.id){
      return checkReportAuthorization(sectionMetadata.id);
    }

    const browselistConfig:IBrowseListConfig = this.browseListConfigService.getConfig(sectionMetadata.browselistCode);
    return checkReportAuthorization(browselistConfig.reportId);
  }

  private get hasDashboardGroups():boolean {
    return !!this.dashboardMetadata.groups;
  }

  // reload all sections + expanded groups sections
  public startSearch = (filters?:IObject, sections?:IDashboardSection[], group?:IDashboardGroup):void => {
    this.saveStateParams(filters);
    if (filters) {
      this.dashboardService.appliedFilters = filters;
    }
    this.dashboardService.createReportRefs(this.sections);

    // expand the first KPI group when the search button is clicked and all groups are collapsed
    if(!group){
      this.expandFirstGroup();
    }

    // include sections from expanded groups into the search
    let sectionsToLoad:IDashboardSection[] = [];
    if (!sections) {
      sectionsToLoad = sectionsToLoad
        .concat(this.getExpandedGroupsSections())
        .concat(this.sections.filter((section:IDashboardSection) => !section.collapsed))
        .concat(this.sections.filter((section:IDashboardSection) => section.collapsed));
    } else {
      sectionsToLoad = sections;
    }

    this.sectionsToLoad = sectionsToLoad; // load a limited number of sections or load all sections (when sections=undefined)

    _.each(this.sectionsToLoad, (section:IDashboardSection) => this.clearDataForSection(section));

    this.loadNecessaryData(this.sectionsToLoad)
      .then(() => setTimeout(() => this.startConcurrentSearch(this.sectionsToLoad, !!sections)));
  };

  // this refreshes one or multiple sections
  // the refresh can be triggered: 1) from a group; 2) from a sections belonging to a group; 3) from a section not belonging to a group
  public refreshSections(sections:IDashboardSection[], group?:IDashboardGroup):void {
    if (!this.validator) {
      this.validator = () => Promise.resolve(true);
    }

    this.validator()
      .then(() => {
        if (sections.length > 1) {
          // complex search
          this.startSearch(this.dashboardService.appliedFilters, sections, group);
        } else {
          // simple section data load
          this.saveStateParams(this.dashboardService.appliedFilters);
          this.sectionsToLoad = sections;
          this.loadNecessaryData(sections)
            .then(() => setTimeout(() => this.loadDataForSection(this.sectionsToLoad[0], true)));
        }
      });
  }

  private loadNecessaryData(sectionsToLoad:IDashboardSection[]):Promise<[IDashboardMetadataReports, IDashboardStoredReports, void] | void> {
    // load the report metadata, stored reports, and layout settings for the given sections
    this.loading = true;
    return Promise.all([
      this.dashboardService.loadReportsMetadata(sectionsToLoad),
      this.dashboardService.loadStoredReports(sectionsToLoad),
      this.dashboardService.loadLayoutSettings(this.dashboardCode, this.authService.getCurrentUser()?.id, sectionsToLoad)
    ])
      .then(util.spread((dashboardMetadataReports:IDashboardMetadataReports, dashboardStoredReports:IDashboardStoredReports) => {
        this.dashboardMetadataReports = dashboardMetadataReports;
        this.dashboardStoredReports = dashboardStoredReports;
      }))
      .finally(() => {
        this.loading = false;
      });
  }

  private startConcurrentSearch(sectionsToLoad:IDashboardSection[], reload?:boolean):void {
    const searchQueues = [];
    let currentSearchIdx = 0;

    const doNextSearch = () => {
      if (currentSearchIdx >= sectionsToLoad.length) {
        return;
      }

      return this.loadDataForSection(sectionsToLoad[ currentSearchIdx++ ], reload)
        .then(() => {
          // if there is still a search operation available, take that next one...
          if (currentSearchIdx < sectionsToLoad.length) {
            return doNextSearch();
          }
        })
        .catch(util.noop);
    };

    // create 'NUM_CONCURRENT_SEARCHES' queues so that there's only a limited number of searches going on at the same time...
    for (let i = 0; i < NUM_CONCURRENT_SEARCHES; i++) {
      searchQueues.push(
        Promise.resolve()
          .then(doNextSearch)
      );
    }
  }

  public cancelSearch():void {
    this.loading = false;

    // cancel search in group sections
    _.chain(this.groups)
      .filter((group:IDashboardGroup) => group.loading)
      .each((group:IDashboardGroup) => {
        _.each(group.sections, (section:IDashboardSection) => {
          this.reportDataService.cancelLoadData(section.id);
        });
      });

    // cancel sections search
    _.chain(this.sections)
      .filter((section:IDashboardSection) => section.report.loading)
      .each((section:IDashboardSection) => this.reportDataService.cancelLoadData(section.id));
  }

  public collapseAll():void {
    // collapse all group sections
    _.chain(this.groups)
      .filter((group:IDashboardGroup) => !group.collapsed)
      .each((group:IDashboardGroup) => {
        _.each(group.sections, (section:IDashboardSection) => {
          this.dashboardService.toggleSection(section, true, true);
        });
      });

    // collapse all sections
    _.chain(this.sections)
      .filter((section:IDashboardSection) => !section.collapsed)
      .each((section:IDashboardSection) => this.dashboardService.toggleSection(section, true, true));
  }

  public registerValidator = (validator:IDashboardValidatorFn):void => {
    this.validator = validator;
  };

  public updateFilters = (filters:IObject):void => {
    this.dashboardService.appliedFilters = filters;
    this.subtitleComponent.inputs.filters = filters;
    this.dashboardService.createReportRefs(this.sections);
    if(this.groups) {
      _.each(this.groups, (group:IDashboardGroup) => {
        this.dashboardService.createReportRefs(group.sections);
      });
    }
  };

  public saveStateParams(filterModel:any):void {
    // extract filters values from the model
    const mapping:IObject = this.dashboardService.getExtraMapping();
    const filters:IObject = {};
    _.each(mapping, (dashboardKey:string, filterKey:string) => {
      const filterValue:any = util.getValue(filterModel, dashboardKey);
      if (filterValue) {
        filters[ filterKey ] = filterValue;
      }
    });

    const defaultFilters:IObject = this.dashboardService.filtersToDashboardFilters(this.dashboardMetadata?.fixedFilters);
    const dashboardFilters:IObject = this.dashboardService.filtersToDashboardFilters(filters);

    const paramsForState:RawParams = {
      filters: this.reportFiltersUtil.filtersToParam(dashboardFilters, defaultFilters)
    };

    this.stateService.go(this.stateService.current, paramsForState, { location: true });
  }

  public getChartLink():string {
    const params:any = {};
    if (this.dashboardService.appliedFilters?.bp?.companyCountryId) {
      params.bp = this.dashboardService.appliedFilters?.bp?.companyCountryId;
    }
    if (this.dashboardService.appliedFilters?.bpGroup?.id) {
      params.bpGroup = this.dashboardService.appliedFilters?.bpGroup?.id;
    }
    if (this.dashboardService.appliedFilters?.currency?.id && this.dashboardService.appliedFilters?.currency?.id !== 'EUR') {
      params.currency = this.dashboardService.appliedFilters?.currency?.id;
    }
    if (this.dashboardService.appliedFilters?.customerCompany?.id) {
      params.customer = this.dashboardService.appliedFilters?.customerCompany?.id;
    }
    if (this.dashboardService.appliedFilters?.customer?.id) {
      params.webshop = this.dashboardService.appliedFilters?.customer?.id;
    }
    switch (this.chartLink) {
      case Chart.BP:
        return this.stateService.href(STATE_CHART_BP, params);
      case Chart.CUSTOMER:
        return this.stateService.href(STATE_CHART_CUSTOMER, params);
      default:
        return '';
    }
  }

  public goToChart(event:Event):void {
    const showChartErrorMessage = (label:string):void => {
      this.messageService.showErrorDialog(
        this.translationService.instant('GA.VALIDATION.VALIDATION_FAILED'),
        this.translationService.instant('GA.VALIDATION.REQUIRED', { label })
      );
    };

    switch (this.chartLink) {
      case Chart.BP:
        // when bp or bp group filter is not selected in dashboard then prevent redirect link
        if (!this.dashboardService.appliedFilters?.bp?.id && !this.dashboardService.appliedFilters?.bpGroup?.id) {
          event.preventDefault();
          return showChartErrorMessage('BP or BP group');
        }
        break;

      case Chart.CUSTOMER:
        // when customer or customer company filter is not selected in dashboard then prevent redirect link
        if (!this.dashboardService.appliedFilters?.customer?.id && !this.dashboardService.appliedFilters?.customerCompany?.id) {
          event.preventDefault();
          return showChartErrorMessage('Customer or Customer company');
        }
        break;

      default:
        event.preventDefault();
        return;
    }
  }

  private onDashboardCodeChange():void {
    this.sections = [];
    this.screen = `${this.category}.${this.dashboardCode}`.toUpperCase();
    this.dashboardMetadata = this.dashboardMetadataService.getMetaData(this.dashboardCode);
    this.dashboardService.dashboardMetadata = this.dashboardMetadata;
    this.filters = _.extend(this.dashboardService.filtersToDashboardFilters(this.dashboardMetadata?.fixedFilters), this.filters || {});
    this.title = this.dashboardMetadata.title;
    this.comments = this.dashboardMetadata.comments;
    this.chartLink = this.dashboardMetadata.chartLink;
    this.titleService.setTitle(this.title);
    this.installComponents();

    // install dashboard groups or sections
    if(this.hasDashboardGroups){
      this.installGroups();
    } else {
      this.installSections();
    }
  }

  private loadDataForSection(section:IDashboardSection, reload?:boolean):Promise<any> {

    const reportId:number = section.report.browseListConfig? section.report.browseListConfig.reportId : section.report.id;

    if (reportId < 0) { //report not done yet
      return Promise.resolve();
    }

    if (!this.dashboardService.appliedFilters || section.report.loading) {
      return Promise.resolve();
    }

    if (!reload && (section.report.appliedFilters === this.dashboardService.appliedFilters)) {
      return Promise.resolve();
    }

    const dashboardSectionMetadata:IDashboardSectionMetadata = this.dashboardService.getDashboardSectionMetadata(section);
    if (!this.validateFilters(dashboardSectionMetadata, this.dashboardService.appliedFilters)) {
      section.collapsed = false;
      return Promise.resolve();
    }

    // we keep this variable to make sure that the auto-cancel of the previous
    // load operation doesn't hide the loading indicator
    const reportWasLoading:boolean = section.report.loading;

    const onError = ():Promise<string> => {
      if (!reportWasLoading) {
        section.report.rowCount = section.report.indicator = undefined;
        section.report.loading = false;
        this.setAnySectionLoading();
        this.updateGroupStatus();
      }

      return Promise.reject('cancelled');
    };

    return section.report.init
      .then((sectionFailedToLoad:void | IDashboardSection):Promise<any> => {
        if ((<IDashboardSection>sectionFailedToLoad)?.id) {
          return onError();
        }
        section.report.loading = true;
        this.setAnySectionLoading();
        this.updateGroupStatus();
      })
      .then(() => {
        section.report.appliedFilters = this.dashboardService.appliedFilters;
        section.report.queryFilters = this.dashboardService.makeSectionQueryFilters(dashboardSectionMetadata, this.dashboardMetadata, section.report.metadata, section.report.queryFilters);
        return this.reportDataService.loadData(section.id, reportId, section.report.queryFilters, section.report.aggregates, section.report.gridMetadata?.columns);
      })
      .then((result:IReportResponse) => {
        section.report.subColumns = result.subColumns;
        section.report.data = result.data;
        this.calculateRowCount(section);
        section.extraIcon = this.updateExtraIcon(section);
      })
      .then(() => {
        section.report.loading = false;
        this.setAnySectionLoading();
        this.updateGroupStatus();
      })
      .catch(onError);
  }

  private clearDataForSection(section:IDashboardSection):void {
    section.report.subColumns = undefined;
    section.report.data = undefined;
    section.report.rowCount = section.report.indicator = undefined;
  }

  private calculateRowCount(section:IDashboardSection):void {
    if (!(section.report.data && section.report.data.data)) {
      section.report.rowCount = section.report.indicator = undefined;
      return;
    }

    // use this for calculating the group count
    section.report.rowCount = section.report.data.data.length;

    let indicator:string | number;
    if (section.report.indicatorFn) {
      indicator = section.report.indicatorFn(section.report.data, this.rowCountCtx());
    } else {
      const dashboardSectionMetadata:IDashboardSectionMetadata = this.dashboardService.getDashboardSectionMetadata(section);
      const firstColumn:string | IDashboardSectionColumn = dashboardSectionMetadata.columns && dashboardSectionMetadata.columns[ 0 ];
      if (_.isObject(firstColumn) && (<IDashboardSectionColumn>firstColumn).groupingSequence) {
        const columnName:string = (<IDashboardSectionColumn>firstColumn).name;
        //calculate the distinct count of the first column
        indicator =
          _.chain(section.report.data.data)
            .map(r => _.isObject(r[ columnName ])? (<IReportValue>r[ columnName ]).value : r[ columnName ])
            .uniq()
            .value()
            .length;
      } else {
        indicator = section.report.data.data.length;
      }
    }

    // add parenthesis when indicator is numerical, when string is expected to have already parenthesis
    section.report.indicator = _.isNumber(indicator) ? `(${indicator})` : indicator;
  }

  // watch for any loading section and update main search button
  private setAnySectionLoading():void {
    let allSections:IDashboardSection[] = _.clone(this.sections);
    if(this.groups) {
      _.each(this.groups, (group:IDashboardGroup) => {
        allSections = allSections.concat(group.sections);
      });
    }

    this.loading = _.reduce(allSections, (memo:boolean, section:IDashboardSection) => memo || section.report.loading, false);
  }

  // watch for any loading section in the group and update group spinner and row count
  private updateGroupStatus():void {
    // show spinner for all groups marked for loading
    if(this.groups){
      _.each(this.groups, (group:IDashboardGroup) => {
        // update spinner before and after group sections finish loading
        group.loading = _.reduce(group.sections, (memo:boolean, section:IDashboardSection) => memo || section.report.loading, false);

        // calculate group counters after group sections finish loading
        if(!group.loading){
          group.rowCount = _.reduce(group.sections, (memo:number, section:IDashboardSection) => memo + section.report.rowCount, 0);
          if(util.notNullOrUndefined(group.rowCount)){
            group.indicator = `(${group.rowCount})`;
          }
        }
      });
    }
  }

  // check to see that the applied filters contain at least one of the required filters (if any)
  private validateFilters(dashboardSectionMetadata:IDashboardSectionMetadata, appliedFilters:any):boolean {
    if (!dashboardSectionMetadata.validations) {
      return true;
    }

    return _.chain(dashboardSectionMetadata.validations)
      .forEach((validation:IDashboardSectionValidation) => {
        validation.invalid = !validation.expression(appliedFilters);
      })
      .filter((validation:IDashboardSectionValidation) => validation.invalid)
      .value().length === 0;
  }

  private updateExtraIcon(section:IDashboardSection):IDashboardExtraIcon {
    if (!section.report.extraStatus) {
      return;
    }
    if (section.report.extraStatus.newValue) {
      switch (section.report.extraStatus.checkType) {
        case IDashboardExtraSectionStatusValueCheckType.GT:
          return section.report.rowCount > 0? this.getExtraIcon(section.report.extraStatus.newValue) : undefined;
        case IDashboardExtraSectionStatusValueCheckType.EQ:
          return section.report.rowCount === 0? this.getExtraIcon(section.report.extraStatus.newValue) : undefined;
        default:
          return undefined;
      }
    }
    return this.getExtraIcon(section.report.extraStatus.defaultValue);
  }

  private getExtraIcon(value:IDashboardExtraSectionStatusValue):IDashboardExtraIcon {
    switch (value) {
      case IDashboardExtraSectionStatusValue.AVAILABLE:
        return ICONS[ 0 ];
      case IDashboardExtraSectionStatusValue.OPTIONAL_NOT_AVAILABLE:
        return ICONS[ 1 ];
      case IDashboardExtraSectionStatusValue.MANDATORY_NOT_AVAILABLE:
        return ICONS[ 2 ];
      default:
        return undefined;
    }
  }

  private rowCountCtx():IRowCountCtx {
    return {
      formattingService: this.formattingService
    };
  }

  private createSection(report:IDashboardSectionMetadata):IDashboardSection {
    const section:IDashboardSection = {
      id: `section${this.sectionIdx++}`,
      key: report.key,
      code: report.code,
      screen: this.screen,
      name: `${this.screen}.${report.key}.TITLE`,
      collapsed: true,
      report: {
        id: report.id,
        browseListCode: report.browselistCode,
        browseListConfig: report.browselistCode && this.browseListConfigService.getConfig(report.browselistCode),
        sort: report.sort,
        aggregates: report.aggregates,
        extraStatus: report.extraStatus,
        indicatorFn: report.indicatorFn,
        gridHeight: report.gridHeight,
        // if there is stored report but no columns defined then use stored report view, columns will be priority than stored report view
        storedReport: !report.columns?.length ? report.storedReport : undefined,
        validations: report.validations,
        layoutGridCode: `${this.dashboardCode}_${report.key}`
      },
      extraTextValue: report.extraTextValue,
      partialRender: report.partialRender
    };

    if (report.infoComponent?.name) {
      section.report.infoComponent = {
        name: report.infoComponent.name,
        inputs: this.getInfoComponentInputs(section, report.infoComponent.inputs),
        outputs: report.infoComponent.outputs
      };
    }

    return section;
  }

  public getInfoComponentInputs(section:IDashboardSection, extraInputs?:IDashboardComponent['inputs']):IObject {
    return {
      filters: _.extend(this.filters, this.dashboardService.appliedFilters || {}),
      loading: this.loading,
      section,
      ...extraInputs
    };
  }

  private getExpandedGroupsSections():IDashboardSection[] {
    const expandedGroups:IDashboardGroup[] = this.groups.filter((group:IDashboardGroup) => !group.collapsed);
    const expandedGroupSections:IDashboardSection[] = [];
    _.each(expandedGroups, (group:IDashboardGroup) => {
      _.each(group.sections, (section:IDashboardSection) => {
        expandedGroupSections.push(section);
      });
    });

    return expandedGroupSections;
  }

  private expandFirstGroup():void {
    if(this.dashboardMetadata?.groups?.length && !_.findWhere(this.groups, { collapsed: false })){
      this.groups[0].collapsed = false;
    }
  }

  // remove the dashboard sections associated with the report IDs which are not found in the metadata response
  public removeSections(reportIds:number[]):void {
    // remove sections when reportId or browseListConfig.reportId matches
    const filterSections:Function = (sections:IDashboardSection[], reportId:number):IDashboardSection[] => _.filter(sections, (section:IDashboardSection) => section.report.id ? section.report.id !== reportId : section.report.browseListConfig.reportId !== reportId);

    _.each(reportIds, (reportId:number) => {
      // remove the section
      this.sections = filterSections(this.sections, reportId);

      // remove from the current sectionsToLoad
      this.sectionsToLoad = filterSections(this.sectionsToLoad, reportId);

      // remove the section from groups
      _.chain(this.groups)
        .each((group:IDashboardGroup) => {
          group.sections = filterSections(group.sections, reportId);
        });
    });
  }

  public ngOnDestroy():void {
    this.bodyClassService.remove('dashboard');
    this.eventHandler.unsubscribe();
  }
}